import React from "react";
import { TLBaseShape, RecordProps, T, BaseBoxShapeUtil, Editor, TLResizeInfo } from "@tldraw/tldraw";
import { getDashArray, getDrawHeartPath, getHeartPath } from "../new-geo-shape-helpers";
import { HOVER_EFFECT } from "./constants";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { defaultScale } from "../constants";

type CustomHeartUtilType = TLBaseShape<
  "customHeart",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    shadowColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    strokeAlign?: "inside" | "center" | "outside";
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
  }
>;

export class CustomHeartUtil extends BaseBoxShapeUtil<CustomHeartUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customHeart" as const;
  static override props: RecordProps<CustomHeartUtilType> = {
    w: T.number,
    h: T.number,
    fill: T.string,
    outlineColor: T.string,
    shadowColor: T.string,
    dash: T.any,
    transparency: T.number,
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any,
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    filterBlur: T.optional(T.number),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    scale: T.optional(T.number),
  };

  // Default properties for the shape
  getDefaultProps(): CustomHeartUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      shadowColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  // Set up shape bounds and dimensions
  getGeometry(shape: CustomHeartUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  hideSelectionBoundsFg(_shape: CustomHeartUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomHeartUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomHeartUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomHeartUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: CustomHeartUtilType, info: TLResizeInfo<CustomHeartUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  // Method to render the shape based on props
  component(shape: CustomHeartUtilType) {
    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = shape.props;

    const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
    const innerShadowFilterId = `inner-shadow-${shape.id}`; // Unique ID for the inner shadow filter
    const pathData = dash === "solid" ? getHeartPath(w, h) : getDrawHeartPath(w, h, 2, shape.id);
    const dashArray = strokeDasharray || getDashArray(dash);

    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }

  addDefaultColorMix(shape: CustomHeartUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomHeartUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomHeartUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  // Indicator function to display shape in editor
  indicator(shape: CustomHeartUtilType) {
    const {
      w,
      h,
      borderWidth,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      isHiddenBorder,
      isShowIndicator,
    } = shape.props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={getHeartPath(w, h)}
        transform={`translate(${isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0}, ${
          isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0
        })`}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}
